import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, MapPinned, Pencil, Plus, Power } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { api, formatApiError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { EmptyState, ErrorState, LoadingState, StatusBadge } from "../components/AsyncState";
import { Field, PageHeader } from "../components/PageHeader";

export type Unit = {
  id: string;
  code: string;
  name_ar: string;
  type: UnitType;
  parent_id: string | null;
  status: "active" | "inactive";
  sort_order: number;
  version: number;
  child_count: number;
  household_count: number;
  agent_count: number;
};

type UnitType = "governorate" | "district" | "area" | "neighborhood" | "residential_unit" | "custom";

const typeLabels: Record<UnitType, string> = {
  governorate: "محافظة",
  district: "مديرية / مدينة",
  area: "منطقة",
  neighborhood: "حي",
  residential_unit: "وحدة سكنية",
  custom: "وحدة مخصصة"
};

type FormState = {
  id?: string;
  code: string;
  nameAr: string;
  type: UnitType;
  parentId: string;
  sortOrder: string;
  status: "active" | "inactive";
  version?: number;
  originalParentId?: string | null;
};

const initialForm: FormState = {
  code: "",
  nameAr: "",
  type: "residential_unit",
  parentId: "",
  sortOrder: "0",
  status: "active"
};

export function UnitsPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<FormState | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const units = useQuery({
    queryKey: ["units"],
    queryFn: async () => (await api<Unit[]>("/units/tree")).data
  });
  const orderedUnits = useMemo(() => buildOrderedUnits(units.data ?? []), [units.data]);

  const save = useMutation({
    mutationFn: async (form: FormState) => {
      if (!form.id) {
        return (
          await api<{ id: string }>("/units", {
            method: "POST",
            body: {
              code: form.code,
              nameAr: form.nameAr,
              type: form.type,
              parentId: form.parentId || null,
              sortOrder: Number(form.sortOrder)
            }
          })
        ).data;
      }
      let version = form.version ?? 1;
      const nextParent = form.parentId || null;
      if (nextParent !== form.originalParentId) {
        const moved = await api<{ version: number }>(`/units/${form.id}/move`, {
          method: "POST",
          body: {
            newParentId: nextParent,
            expectedVersion: version,
            reason: "تعديل الهيكل التنظيمي من شاشة إدارة الوحدات"
          }
        });
        version = moved.data.version;
      }
      return (
        await api<{ id: string }>(`/units/${form.id}`, {
          method: "PATCH",
          body: {
            code: form.code,
            nameAr: form.nameAr,
            type: form.type,
            sortOrder: Number(form.sortOrder),
            status: form.status,
            expectedVersion: version
          }
        })
      ).data;
    },
    onSuccess: async (_, form) => {
      setNotice(form.id ? "تم حفظ الوحدة وتوثيق التغيير." : "تمت إضافة الوحدة بنجاح.");
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ["units"] });
    }
  });

  const toggle = useMutation({
    mutationFn: async (unit: Unit) =>
      api(`/units/${unit.id}`, {
        method: "PATCH",
        body: { status: unit.status === "active" ? "inactive" : "active", expectedVersion: unit.version }
      }),
    onSuccess: async () => {
      setNotice("تم تحديث حالة الوحدة مع الاحتفاظ بتاريخها.");
      await queryClient.invalidateQueries({ queryKey: ["units"] });
    }
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (editing) save.mutate(editing);
  }

  return (
    <div className="page">
      <PageHeader
        title="الوحدات الجغرافية والسكنية"
        description="إدارة التسلسل من المحافظة حتى الوحدة السكنية. التعطيل يحفظ العلاقات والسجل التاريخي."
        actions={
          can("area.manage") ? (
            <button className="button" type="button" onClick={() => setEditing(initialForm)}>
              <Plus aria-hidden="true" /> إضافة وحدة
            </button>
          ) : undefined
        }
      />
      {notice ? (
        <div className="success-panel" role="status">
          <MapPinned aria-hidden="true" /> <p>{notice}</p>
        </div>
      ) : null}
      {editing ? (
        <section className="content-card management-form" aria-labelledby="unit-form-title">
          <div className="section-head">
            <div>
              <h2 id="unit-form-title">{editing.id ? "تعديل الوحدة" : "إضافة وحدة جديدة"}</h2>
              <p>اختر المستوى والأب المطابق ليبقى التسلسل صحيحًا.</p>
            </div>
          </div>
          <form className="form-grid" onSubmit={submit}>
            <Field label="الاسم العربي" htmlFor="unit-name">
              <input
                id="unit-name"
                value={editing.nameAr}
                onChange={(event) => setEditing({ ...editing, nameAr: event.target.value })}
                required
              />
            </Field>
            <Field label="الرمز" htmlFor="unit-code">
              <input
                id="unit-code"
                dir="ltr"
                value={editing.code}
                onChange={(event) => setEditing({ ...editing, code: event.target.value })}
                required
              />
            </Field>
            <Field label="المستوى" htmlFor="unit-type">
              <select
                id="unit-type"
                value={editing.type}
                onChange={(event) => setEditing({ ...editing, type: event.target.value as UnitType })}
              >
                {Object.entries(typeLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="الوحدة الأب"
              htmlFor="unit-parent"
              hint="المحافظة فقط يمكن أن تكون في المستوى الأعلى."
            >
              <select
                id="unit-parent"
                value={editing.parentId}
                onChange={(event) => setEditing({ ...editing, parentId: event.target.value })}
              >
                <option value="">بدون وحدة أب</option>
                {orderedUnits
                  .filter(({ unit }) => unit.id !== editing.id)
                  .map(({ unit, depth }) => (
                    <option key={unit.id} value={unit.id}>
                      {"— ".repeat(depth)} {unit.name_ar} ({typeLabels[unit.type]})
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="ترتيب العرض" htmlFor="unit-order">
              <input
                id="unit-order"
                type="number"
                min="0"
                value={editing.sortOrder}
                onChange={(event) => setEditing({ ...editing, sortOrder: event.target.value })}
                required
              />
            </Field>
            {editing.id ? (
              <Field label="الحالة" htmlFor="unit-status">
                <select
                  id="unit-status"
                  value={editing.status}
                  onChange={(event) =>
                    setEditing({ ...editing, status: event.target.value as FormState["status"] })
                  }
                >
                  <option value="active">نشطة</option>
                  <option value="inactive">معطلة</option>
                </select>
              </Field>
            ) : null}
            {save.isError ? (
              <div className="inline-alert form-span" role="alert">
                {formatApiError(save.error)}
              </div>
            ) : null}
            <div className="dialog-actions form-span">
              <button className="button button--secondary" type="button" onClick={() => setEditing(null)}>
                إلغاء
              </button>
              <button className="button" type="submit" disabled={save.isPending}>
                {save.isPending ? "جارٍ الحفظ…" : "حفظ الوحدة"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {units.isPending ? (
        <LoadingState />
      ) : units.isError ? (
        <ErrorState error={units.error} retry={() => void units.refetch()} />
      ) : orderedUnits.length === 0 ? (
        <EmptyState title="لا توجد وحدات" description="أضف المحافظة الأولى لبدء الهيكل الجغرافي." />
      ) : (
        <section className="content-card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الوحدة</th>
                  <th>المستوى</th>
                  <th>الأسر</th>
                  <th>الوكلاء</th>
                  <th>الحالة</th>
                  {can("area.manage") ? <th>الإجراءات</th> : null}
                </tr>
              </thead>
              <tbody>
                {orderedUnits.map(({ unit, depth }) => (
                  <tr key={unit.id}>
                    <td>
                      <div className="unit-name" style={{ paddingInlineStart: `${depth * 1.25}rem` }}>
                        <Building2 aria-hidden="true" />
                        <span>
                          <strong>{unit.name_ar}</strong>
                          <small>{unit.code}</small>
                        </span>
                      </div>
                    </td>
                    <td>{typeLabels[unit.type]}</td>
                    <td>{unit.household_count}</td>
                    <td>{unit.agent_count}</td>
                    <td>
                      <StatusBadge status={unit.status} />
                    </td>
                    {can("area.manage") ? (
                      <td>
                        <div className="action-row">
                          <button
                            className="button button--ghost"
                            type="button"
                            onClick={() => setEditing(toForm(unit))}
                          >
                            <Pencil aria-hidden="true" /> تعديل
                          </button>
                          <button
                            className="button button--ghost"
                            type="button"
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate(unit)}
                          >
                            <Power aria-hidden="true" /> {unit.status === "active" ? "تعطيل" : "تنشيط"}
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {toggle.isError ? (
            <div className="inline-alert" role="alert">
              {formatApiError(toggle.error)}
            </div>
          ) : null}
        </section>
      )}
    </div>
  );
}

function toForm(unit: Unit): FormState {
  return {
    id: unit.id,
    code: unit.code,
    nameAr: unit.name_ar,
    type: unit.type,
    parentId: unit.parent_id ?? "",
    originalParentId: unit.parent_id,
    sortOrder: String(unit.sort_order),
    status: unit.status,
    version: unit.version
  };
}

function buildOrderedUnits(units: Unit[]): Array<{ unit: Unit; depth: number }> {
  const children = new Map<string | null, Unit[]>();
  for (const unit of units) {
    const items = children.get(unit.parent_id) ?? [];
    items.push(unit);
    children.set(unit.parent_id, items);
  }
  for (const items of children.values())
    items.sort((a, b) => a.sort_order - b.sort_order || a.name_ar.localeCompare(b.name_ar, "ar"));
  const output: Array<{ unit: Unit; depth: number }> = [];
  const visited = new Set<string>();
  const visit = (parentId: string | null, depth: number) => {
    for (const unit of children.get(parentId) ?? []) {
      if (visited.has(unit.id)) continue;
      visited.add(unit.id);
      output.push({ unit, depth });
      visit(unit.id, depth + 1);
    }
  };
  visit(null, 0);
  for (const unit of units) {
    if (!visited.has(unit.id)) output.push({ unit, depth: 0 });
  }
  return output;
}
